// src/pages/Events/components/VideoPlayer.jsx
// 🎥 Clean enterprise video player - mobile friendly

import { useEffect, useRef, useState } from "react";
import { FiMaximize, FiZoomIn, FiZoomOut, FiAlertCircle, FiInfo } from "react-icons/fi";

export default function VideoPlayer({
  camera,
  recordings = [],
  currentTime,
  setCurrentTime,
  playing,
  playbackSpeed,
  showAnalytics,
  detectionOverlays = [],
  onAddBookmark,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-hide controls on desktop after 3 seconds of inactivity
  useEffect(() => {
    if (isMobile) return; // Always show on mobile

    let timeout;
    const handleActivity = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleActivity);
      container.addEventListener('mouseenter', handleActivity);
      handleActivity(); // Initial show
    }

    return () => {
      clearTimeout(timeout);
      if (container) {
        container.removeEventListener('mousemove', handleActivity);
        container.removeEventListener('mouseenter', handleActivity);
      }
    };
  }, [isMobile]);

  // Find recording segment for current time
  const currentRecording = recordings.find(rec => {
    const start = new Date(rec.start_time);
    const end = new Date(rec.end_time);
    return currentTime >= start && currentTime <= end;
  });

  // Load video - simple approach like working version
  useEffect(() => {
    if (!currentRecording || !videoRef.current) {
      setIsReady(false);
      return;
    }

    const video = videoRef.current;
    const url = currentRecording.file_url;

    const handleEvent = (e) => {
      if (e.type === 'error') {
        const errorCodes = {
          1: 'MEDIA_ERR_ABORTED',
          2: 'MEDIA_ERR_NETWORK', 
          3: 'MEDIA_ERR_DECODE',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED'
        };
        const errMsg = video.error ? errorCodes[video.error.code] : 'Unknown';
        console.error('Video error:', errMsg);
        setError(errMsg);
        setIsReady(false);
      }
      
      if (e.type === 'canplay' || e.type === 'canplaythrough') {
        setIsReady(true);
        setError(null);
      }
    };

    const events = [
      'loadstart', 'progress', 'suspend', 'abort', 'error',
      'emptied', 'stalled', 'loadedmetadata', 'loadeddata',
      'canplay', 'canplaythrough', 'playing', 'waiting',
      'seeking', 'seeked', 'ended', 'durationchange',
      'timeupdate', 'play', 'pause', 'ratechange', 'resize', 'volumechange'
    ];

    events.forEach(evt => video.addEventListener(evt, handleEvent));

    video.src = url;
    
    try {
      video.load();
    } catch (err) {
      console.error('video.load() failed:', err);
    }

    const handleLoadedMetadata = () => {
      const recordingStart = new Date(currentRecording.start_time);
      const offset = (currentTime - recordingStart) / 1000;
      video.currentTime = Math.max(0, offset);
    };
    
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      events.forEach(evt => video.removeEventListener(evt, handleEvent));
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [currentRecording, currentTime]);

  // Handle playback
  useEffect(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;

    if (playing && isReady && video.readyState >= 3) {
      video.play()
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('Play failed:', err);
          }
        });
    } else if (!playing) {
      video.pause();
    }
  }, [playing, isReady]);

  // Handle playback speed
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  // Update currentTime as video plays
  useEffect(() => {
    if (!videoRef.current || !playing || !currentRecording) return;

    const video = videoRef.current;
    const handleTimeUpdate = () => {
      const recordingStart = new Date(currentRecording.start_time);
      const newTime = new Date(recordingStart.getTime() + video.currentTime * 1000);
      setCurrentTime(newTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [playing, currentRecording, setCurrentTime]);

  // Draw analytics overlays
  useEffect(() => {
    if (!showAnalytics || !canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;

    try {
      canvas.width = video.videoWidth || video.clientWidth;
      canvas.height = video.videoHeight || video.clientHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const currentOverlays = detectionOverlays.filter(det => {
        const detTime = new Date(det.timestamp);
        const diff = Math.abs(detTime - currentTime);
        return diff < 1000;
      });

      currentOverlays.forEach(det => {
        if (!det.bounding_box) return;
        const { x, y, w, h } = det.bounding_box;

        ctx.strokeStyle = '#3B82F6';
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, w, h);

        ctx.fillStyle = '#3B82F6';
        ctx.fillRect(x, y - 25, w, 25);
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '14px sans-serif';
        ctx.fillText(det.label || 'Detection', x + 5, y - 8);

        if (det.confidence) {
          ctx.fillText(`${Math.round(det.confidence * 100)}%`, x + 5, y + h + 20);
        }
      });
    } catch (err) {
      console.warn("Analytics overlay disabled (CORS):", err.message);
    }
  }, [showAnalytics, detectionOverlays, currentTime]);

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative flex-1 bg-black flex items-center justify-center overflow-hidden group"
    >
      {/* No Recording Message */}
      {!currentRecording && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <div className="text-center px-4">
            <FiAlertCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-50" />
            <div className="text-lg sm:text-xl font-bold mb-2">No Recording Available</div>
            <div className="text-xs sm:text-sm text-gray-400">
              No recording found for selected time
            </div>
            {recordings.length > 0 && (
              <div className="text-xs text-gray-500 mt-2">
                {recordings.length} recording(s) available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Video Element */}
      {currentRecording && (
        <video
          ref={videoRef}
          className="max-w-full max-h-full"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
          }}
          playsInline
          controls
        />
      )}

      {/* Analytics Overlay Canvas */}
      {showAnalytics && currentRecording && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
          }}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white">
          <div className="text-center max-w-md px-4">
            <FiAlertCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-red-500" />
            <div className="text-lg sm:text-xl font-bold mb-2 text-red-500">Playback Error</div>
            <div className="text-xs sm:text-sm text-gray-400 mb-4">{error}</div>
            {currentRecording && !isMobile && (
              <div className="text-xs text-gray-500 bg-black/50 p-3 rounded">
                <div className="mb-1">Recording URL:</div>
                <div className="font-mono break-all text-[10px]">{currentRecording.file_url}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Controls - Only visible on hover (desktop) or always (mobile) */}
      {currentRecording && (
        <div 
          className={`absolute top-0 left-0 right-0 p-2 sm:p-4 flex items-start justify-between transition-opacity duration-300 ${
            showControls || isMobile ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Camera Info - Compact */}
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg flex items-center gap-2 text-xs sm:text-sm hover:bg-black/80 transition-colors"
          >
            <FiInfo className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{camera?.name || 'Camera'}</span>
            <span className="sm:hidden">Info</span>
          </button>

          {/* Zoom & Fullscreen Controls */}
          <div className="flex items-center gap-1 sm:gap-2">
            {!isMobile && (
              <>
                <button
                  onClick={() => setZoom(Math.max(1, zoom - 0.25))}
                  className="p-1.5 sm:p-2 bg-black/60 backdrop-blur-sm text-white rounded hover:bg-black/80 disabled:opacity-30 transition-colors"
                  disabled={zoom <= 1}
                  title="Zoom out"
                >
                  <FiZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>

                <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white rounded text-xs">
                  {Math.round(zoom * 100)}%
                </span>

                <button
                  onClick={() => setZoom(Math.min(4, zoom + 0.25))}
                  className="p-1.5 sm:p-2 bg-black/60 backdrop-blur-sm text-white rounded hover:bg-black/80 disabled:opacity-30 transition-colors"
                  disabled={zoom >= 4}
                  title="Zoom in"
                >
                  <FiZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </>
            )}

            <button
              onClick={toggleFullscreen}
              className="p-1.5 sm:p-2 bg-black/60 backdrop-blur-sm text-white rounded hover:bg-black/80 transition-colors"
              title="Fullscreen"
            >
              <FiMaximize className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Info Panel - Expandable */}
      {showInfo && currentRecording && (
        <div className="absolute top-12 sm:top-16 left-2 sm:left-4 bg-black/80 backdrop-blur-sm text-white p-3 sm:p-4 rounded-lg max-w-xs text-xs sm:text-sm space-y-2 animate-fadeIn">
          {/* Camera */}
          <div>
            <div className="font-bold text-sm sm:text-base">{camera?.name || 'Camera'}</div>
            {camera?.location && (
              <div className="text-gray-400 text-xs">{camera.location}</div>
            )}
          </div>

          {/* Recording Time */}
          <div className="text-gray-300">
            <div className="text-[10px] sm:text-xs text-gray-500 uppercase">Recording</div>
            <div>
              {new Date(currentRecording.start_time).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })} - {new Date(currentRecording.end_time).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
            <div className="text-gray-500 text-xs">
              {Math.round(currentRecording.duration_s / 60)}min
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full ${isReady ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span>{!isReady ? "Loading..." : (playing ? "Playing" : "Paused")}</span>
          </div>

          {/* Analytics Status */}
          {showAnalytics && (
            <div className="flex items-center gap-2 text-xs text-blue-400">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>Analytics Active</span>
            </div>
          )}

          {/* Close button */}
          <button
            onClick={() => setShowInfo(false)}
            className="w-full mt-2 py-1.5 bg-white/10 hover:bg-white/20 rounded text-xs transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
